import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOrganization } from '@/hooks/use-organization';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { User } from 'lucide-react';
import {
  HabitTracker,
  SupportGroups,
  CoachingSessions,
  Education as MiniCourses,
  RewardsSystem,
  CondensedRewards,
  UpcomingTrips,
  TreatmentSupport,
  IntermittentFastingWidget,
  RecordingMealsWidget,
  RecordingActivityWidget,
  SleepLogsWidget,
  HealthMeasuresWidget
} from '@/components/customer';
import { CompetitionRankingWidget } from '@/components/dashboard/CompetitionRankingWidget';
import { MyGroupCard } from '@/components/customer/MyGroupCard';
import { NameInputDialogWithOTP } from '@/components/auth/NameInputDialogWithOTP';
import { setOnNameInputComplete } from '@/lib/firebase/auth/google';
import type { UserData } from '@/lib/firebase/auth/types';
import { ScoreBadge } from '@/components/customer/ScoreBadge';
import { openNamatScale } from '@/components/customer/score-events';
import { useActiveCustomerPermissions } from '@/hooks/useActiveCustomerPermissions';
import { OrganizationSwitcher } from '@/components/customer/OrganizationSwitcher';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1
  }
};

export function CustomerDashboard() {
  const { userData, user } = useAuth();
  const { t, language } = useLanguage();
  const { 
    organizations, 
    selectedOrganization: organization, 
    switchOrganization 
  } = useOrganization();
  const { hasSectionAccess } = useActiveCustomerPermissions();
  // One habit list for personal and competition habits (namat#36, mobile
  // TodayScreen): each half keeps its own permission, so a member with
  // competitions on but habit tracking off still has somewhere to log.
  const showPersonalHabits = hasSectionAccess('habitTracking');
  const showCompetitions = hasSectionAccess('organizationCompetitions');
  // «مجموعتي» (namat#36, mobile TodayScreen): shown with competitions or
  // support groups on; ranked only when competitions are on. The card itself
  // renders nothing without a group in the active organization.
  const myGroupCard = (showCompetitions || hasSectionAccess('supportGroups')) && (
    <motion.div variants={itemVariants}>
      <MyGroupCard organizationId={organization?.id} withRanking={showCompetitions} />
    </motion.div>
  );
  const habitTracker = (showPersonalHabits || showCompetitions) && (
    <motion.div variants={itemVariants}>
      <HabitTracker
        organizationId={organization?.id}
        showPersonalHabits={showPersonalHabits}
        showCompetitionHabits={showCompetitions}
      />
    </motion.div>
  );
  const [isRewardsExpanded, setIsRewardsExpanded] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [newUserData, setNewUserData] = useState<UserData | null>(null);

  // Show NameInputDialog for Google signups with Arabic names in Latin or missing phone
  useEffect(() => {
    setOnNameInputComplete((userData) => {
      setNewUserData(userData);
      setShowNameDialog(true);
    });
  }, []);

  useEffect(() => {
    if (!userData) return;
    const hasPhone = Boolean(userData.phone);
    const alreadyCompleted = user?.uid ? localStorage.getItem(`profileCompleted_${user.uid}`) === 'true' : false;
    // If phone is missing, prompt user to enter it (names are auto-translated by AI)
    if (!hasPhone && !showNameDialog && !alreadyCompleted) {
      setShowNameDialog(true);
    }
  }, [userData, showNameDialog]);

  // The Namat scale questionnaire opens from the top bar's «مقياس نمط» (#124),
  // which also opens by itself once per session while it is not valid.

  // Get the appropriate name based on language
  const firstName = language === 'ar' ? userData?.firstNameAr : userData?.firstNameEn;

  const handleNameInputComplete = () => {
    setShowNameDialog(false);
    setNewUserData(null);
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Dashboard Header */}
      <div className="space-y-2">
        <div className={`flex flex-col gap-4 ${
          !isRewardsExpanded && hasSectionAccess('pointsRewards') 
            ? organization
              ? 'lg:grid lg:grid-cols-[1fr_0.8fr_1fr] lg:items-center'
              : 'lg:grid lg:grid-cols-[1fr_auto] lg:items-center'
            : organization
              ? 'lg:grid lg:grid-cols-2 lg:items-center lg:justify-between'
              : 'lg:grid lg:grid-cols-[1fr_auto] lg:items-center'
        }`}>
          {/* Mobile: Organization Switcher After Welcome */}
          {organization && (
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              className="w-full lg:hidden order-2"
            >
              <Card className="w-full">
                <CardContent className="px-8 py-4">
                  <OrganizationSwitcher
                    organizations={organizations}
                    selectedOrganization={organization}
                    onSwitchOrganization={switchOrganization}
                  />
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Left: User Welcome Section */}
          <div className="flex items-center gap-4 lg:justify-start order-1 lg:order-1">
            <div className="relative">
              <button
                type="button"
                onClick={openNamatScale}
                aria-label={t('common.namatScale.title')}
                className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7dda55] focus-visible:ring-offset-2"
              >
              <Avatar className="h-[70px] w-[70px] bg-[#7dda55] text-white border-none">
                <AvatarImage src={userData?.photoURL} alt={firstName} className="object-cover" />
                <AvatarFallback className="bg-[#7dda55]">
                  <User className="h-[34px] w-[34px]" />
                </AvatarFallback>
              </Avatar>
              </button>
              <ScoreBadge />
            </div>
            <div className="space-y-1">
              <h1 className={`font-bold tracking-tight ${language === 'en' ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'}`}>
                {t('dashboard.welcomeBack')}, {firstName}! 👋
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                {t('dashboard.dashboardDesc')}
              </p>
            </div>
          </div>

          {/* Center: Organization Switcher for Desktop (when rewards is shown) */}
          {organization && (!isRewardsExpanded && hasSectionAccess('pointsRewards')) && (
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              className="hidden lg:flex lg:justify-center lg:order-2"
            >
              <OrganizationSwitcher
                organizations={organizations}
                selectedOrganization={organization}
                onSwitchOrganization={switchOrganization}
              />
            </motion.div>
          )}

          {/* Right: Organization Switcher for Desktop (when rewards is hidden) */}
          {organization && (isRewardsExpanded || !hasSectionAccess('pointsRewards')) && (
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              className="hidden lg:flex lg:justify-end lg:order-2"
            >
              <Card className="max-w-xs">
                <CardContent className="px-8 py-4">
                  <OrganizationSwitcher
                    organizations={organizations}
                    selectedOrganization={organization}
                    onSwitchOrganization={switchOrganization}
                  />
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Right: Rewards Section */}
          {!isRewardsExpanded && hasSectionAccess('pointsRewards') && (
            <div className={`lg:flex lg:justify-end order-3 ${
              organization
                ? 'lg:order-3' 
                : 'lg:order-2'
            }`}>
              <CondensedRewards 
                onExpandChange={setIsRewardsExpanded}
                organizationId={organization?.id}
              />
            </div>
          )}

          {/* Placeholder to maintain grid when no org and no rewards */}
          {!organization && (isRewardsExpanded || !hasSectionAccess('pointsRewards')) && (
            <div className="hidden lg:block lg:order-2"></div>
          )}
        </div>
      </div>

      <motion.div 
        className="grid gap-4 sm:gap-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Rewards System (Expanded) */}
        {isRewardsExpanded && hasSectionAccess('pointsRewards') && (
          <motion.div
            variants={itemVariants}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <RewardsSystem 
              isExpanded={isRewardsExpanded}
              onExpandChange={setIsRewardsExpanded}
              organizationId={organization?.id}
            />
          </motion.div>
        )}

        {/* Mobile Layout - Custom Order */}
        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:hidden auto-rows-min">
          {hasSectionAccess('healthMeasures') && (
            <motion.div variants={itemVariants}>
              <HealthMeasuresWidget organizationId={organization?.id} />
            </motion.div>
          )}

          {hasSectionAccess('mealLogs') && (
            <motion.div variants={itemVariants}>
              <RecordingMealsWidget organizationId={organization?.id} />
            </motion.div>
          )}

          {habitTracker}

          {hasSectionAccess('intermittentFasting') && (
            <motion.div variants={itemVariants}>
              <IntermittentFastingWidget organizationId={organization?.id} />
            </motion.div>
          )}

          {hasSectionAccess('activityLogs') && (
            <motion.div variants={itemVariants}>
              <RecordingActivityWidget organizationId={organization?.id} />
            </motion.div>
          )}

          {hasSectionAccess('sleepLogs') && (
            <motion.div variants={itemVariants}>
              <SleepLogsWidget organizationId={organization?.id} />
            </motion.div>
          )}

          {hasSectionAccess('supportGroups') && (
            <motion.div variants={itemVariants}>
              <SupportGroups organizationId={organization?.id} />
            </motion.div>
          )}
          
          {/* Organization Competitions - Mobile. Their habits are logged in
              the habit list above (habitTracker). */}
          {showCompetitions && (
            <motion.div variants={itemVariants}>
              <CompetitionRankingWidget organizationId={organization?.id} />
            </motion.div>
          )}

          {myGroupCard}
          
          {hasSectionAccess('coachingSessions') && (
            <motion.div variants={itemVariants}>
              <CoachingSessions organizationId={organization?.id} />
            </motion.div>
          )}
          
          {hasSectionAccess('miniCourses') && (
            <motion.div variants={itemVariants}>
              <MiniCourses organizationId={organization?.id} />
            </motion.div>
          )}
          
          {hasSectionAccess('changeTrips') && (
            <motion.div variants={itemVariants}>
              <UpcomingTrips organizationId={organization?.id} />
            </motion.div>
          )}
          
          {hasSectionAccess('treatmentAid') && (
            <motion.div variants={itemVariants}>
              <TreatmentSupport organizationId={organization?.id} />
            </motion.div>
          )}
        </div>

        {/* Desktop Layout - Original Two-Column Grid */}
        <div className="hidden md:grid grid-cols-2 gap-4 sm:gap-6 auto-rows-min">
          {/* Left Column */}
          <div className="flex flex-col gap-4 sm:gap-6">
            {hasSectionAccess('healthMeasures') && (
              <motion.div variants={itemVariants}>
                <HealthMeasuresWidget organizationId={organization?.id} />
              </motion.div>
            )}

            {hasSectionAccess('mealLogs') && (
              <motion.div variants={itemVariants}>
                <RecordingMealsWidget organizationId={organization?.id} />
              </motion.div>
            )}

            {habitTracker}

            {hasSectionAccess('coachingSessions') && (
              <motion.div variants={itemVariants}>
                <CoachingSessions organizationId={organization?.id} />
              </motion.div>
            )}
            
            {hasSectionAccess('changeTrips') && (
              <motion.div variants={itemVariants}>
                <UpcomingTrips organizationId={organization?.id} />
              </motion.div>
            )}
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-4 sm:gap-6">
            {hasSectionAccess('intermittentFasting') && (
              <motion.div variants={itemVariants}>
                <IntermittentFastingWidget organizationId={organization?.id} />
              </motion.div>
            )}

            {hasSectionAccess('activityLogs') && (
              <motion.div variants={itemVariants}>
                <RecordingActivityWidget organizationId={organization?.id} />
              </motion.div>
            )}

            {hasSectionAccess('sleepLogs') && (
              <motion.div variants={itemVariants}>
                <SleepLogsWidget organizationId={organization?.id} />
              </motion.div>
            )}

            {hasSectionAccess('supportGroups') && (
              <motion.div variants={itemVariants}>
                <SupportGroups organizationId={organization?.id} />
              </motion.div>
            )}
            
            {/* Organization Competitions - Desktop Right Column. */}
            {showCompetitions && (
              <motion.div variants={itemVariants}>
                <CompetitionRankingWidget organizationId={organization?.id} />
              </motion.div>
            )}

            {myGroupCard}
            
            {hasSectionAccess('miniCourses') && (
              <motion.div variants={itemVariants}>
                <MiniCourses organizationId={organization?.id} />
              </motion.div>
            )}
            
            {hasSectionAccess('treatmentAid') && (
              <motion.div variants={itemVariants}>
                <TreatmentSupport organizationId={organization?.id} />
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      <NameInputDialogWithOTP
        isOpen={showNameDialog}
        onClose={() => setShowNameDialog(false)}
        userId={newUserData?.id || ''}
        onComplete={handleNameInputComplete}
      />

      <NameInputDialogWithOTP
        isOpen={showNameDialog}
        onClose={() => setShowNameDialog(false)}
        userId={user?.uid || ''}
        onComplete={() => setShowNameDialog(false)}
      />
    </div>
  );
}